import { ROUTES } from '@/env/routes'
import clsx from 'clsx'
import { memo, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import styles from './index.module.scss'
import { useSidebarMenu } from '@/hooks/sidebar/useSidebarMenu'
import { cn } from '@/styles'
import LogIcon from './assets/log.svg'
import RadioIcon from './assets/radio.svg'
import RadioOnIcon from './assets/radio-on.svg'
import { useCommon } from '@/core/useCommon'
import { AnimatePresence, motion } from 'framer-motion'
import { setUIState, useUIStore } from '@/ui/useUIStore'
import { useIsMobile } from '@/hooks/use-mobile'
import { logout } from '@/store/user/useUserStore'
import { SIDEBAR_MENU } from '@/hooks/sidebar/constant'
import { Language } from '@/hooks/lang/constant'
import { useSelectLang } from '@/hooks/lang/useSelectLang'
import { languageIconMap } from '@/views/LanguageItem/constant'
import UserData from '@/views/User/UserData'
import ChevronRightIcon from '@/views/assets/chevron-right.svg'
import { LAYOUT } from '@/layouts/constant'
import PaymentOverview from '@/views/User/PaymentOverview'

// 側邊欄
const SidebarContent = () => {
  const { t, push } = useCommon()

  const { pathname } = useLocation()
  // 登入頁
  const loginRoute = ROUTES.LOGIN()
  // 側邊欄選單
  const { menusMap, withCloseSidebar } = useSidebarMenu()
  // 語系切換選單
  const [showLangMenu, setShowLangMenu] = useState(false)

  const {
    // languageLabelMap,
    localesOptions,
    locale, // 當前語系
    setLocale,
  } = useSelectLang()

  // 選單列表
  const menuList = useMemo(() => {
    return Object.values(menusMap)
      ?.filter((menu) => !menu.hidden)
      ?.map((menu) => {
        // 選擇語系選單
        if (menu.key === SIDEBAR_MENU.LANG) {
          return {
            ...menu,
            onPress: () => setShowLangMenu(!showLangMenu),
            RightIcon: (
              <ChevronRightIcon
                className={clsx('size-16 shrink-0 text-white transition-transform', {
                  'rotate-90': showLangMenu,
                })}
              />
            ),
            render: () => (
              <AnimatePresence>
                {!showLangMenu ? null : (
                  <motion.div
                    initial={{ height: '0' }}
                    animate={{ height: 'auto', transition: { ease: 'easeInOut', duration: 0.15 } }}
                    exit={{ height: '0', transition: { ease: 'easeInOut', duration: 0.15 } }}
                    className="rounded-8 left-0 flex flex-col gap-4 overflow-hidden bg-white px-8">
                    {localesOptions.map((lang) => (
                      <div
                        key={lang.value}
                        className={cn('flex h-32 items-center justify-between text-[#6D7693] first:mt-4 last:mb-4', {
                          'text-[#0050C8]': locale === lang.value,
                        })}
                        onClick={() => {
                          // 切換語系
                          setLocale(lang.value)
                          setShowLangMenu(false)
                        }}>
                        <div className="flex w-full items-center gap-8">
                          <div className="size-24 shrink-0">{languageIconMap.get(lang.value as Language) && <img src={languageIconMap.get(lang.value as Language)} className="size-full" />}</div>
                          <div className="">{lang.label}</div>
                        </div>

                        <div>{locale === lang.value ? <RadioOnIcon /> : <RadioIcon />}</div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            ),
          }
        }
        return {
          ...menu,
        }
      })
  }, [menusMap, showLangMenu])

  // 特定 router 隱藏側邊欄
  const hidden = useMemo(() => {
    // 登入頁不顯示側邊欄
    return pathname === loginRoute
  }, [pathname])

  return (
    <motion.div
      className={clsx('bg-full fixed top-0 bottom-0 left-0 z-170 flex h-full w-312 flex-col overflow-auto sm:fixed sm:w-280 sm:overflow-hidden', styles.sidebar, { hidden })}
      initial={{ x: '-100%' }}
      animate={{ x: '0%', transition: { ease: 'easeInOut', duration: 0.3 } }}
      exit={{ x: '-100%', transition: { ease: 'easeInOut', duration: 0.3 } }}
      key="content"
      layout>
      <ul className="relative flex flex-1 flex-col gap-8 px-12 pt-52 sm:overflow-hidden sm:px-0 sm:pt-0">
        {/* 代理商首頁 文字(pc 才有) */}
        <li className="hidden shrink-0 cursor-pointer sm:flex sm:h-76 sm:items-center sm:justify-center sm:gap-22 sm:px-12" onClick={() => push(ROUTES.HOME())}>
          <div className={clsx('bg-full size-28', styles.sidebarAgentLogo)} />
          <div className="text-16 text-white">{t('old:dashboard')}</div>
        </li>

        <li className={clsx('hidden min-h-106 w-full shrink-0 flex-col bg-[#1E44AD] sm:flex sm:shrink-0 sm:items-start sm:p-16')}>
          <UserData />

          <PaymentOverview />
        </li>

        <ul className="flex flex-1 flex-col gap-8 overflow-auto sm:-mx-12 sm:gap-12 sm:px-32 sm:pb-160">
          {menuList.map((menu) => {
            const Icon = menu.icon as React.ComponentType<{ className?: string }>

            return (
              <li
                className={cn('rounded-8 text-14 relative flex min-h-48 w-full shrink-0 cursor-pointer flex-col justify-center select-none hover:bg-white/20', {
                  'bg-white hover:bg-white': pathname === (menu.value as string),
                })}
                key={menu.key as string}
                onClick={menu.onPress}>
                <div className="flex h-48 w-full items-center pr-8 pl-12">
                  <div className="flex flex-1 items-center gap-8">
                    <Icon
                      className={cn('size-24 text-[#E3E9FA]', {
                        'text-[#0050C8]': pathname === (menu.value as string),
                      })}
                    />
                    <span
                      className={cn('text-white capitalize', {
                        'text-[#0050C8]': pathname === (menu.value as string),
                      })}>
                      {menu.label}
                    </span>
                  </div>

                  {'RightIcon' in menu && menu.RightIcon}
                </div>

                {'render' in menu && menu?.render?.()}
              </li>
            )
          })}
        </ul>
        <div className="pointer-events-none absolute right-0 bottom-0 left-0 hidden h-100 w-full bg-linear-to-t from-[#3157E0]/60 to-transparent sm:flex" />
      </ul>

      {/* 登出 */}
      <div className="my-32 shrink-0 px-12">
        <div className="rounded-8 hover flex min-h-48 w-full cursor-pointer items-center gap-8 bg-[#013176] pr-8 pl-12 text-white select-none" onClick={() => withCloseSidebar(logout)}>
          <LogIcon className="size-24 text-[#E3E9FA]" />
          <span className="text-14 text-white">{t('old:signOut')}</span>
        </div>
      </div>
    </motion.div>
  )
}

/** 側邊欄 */
export const Sidebar = memo(({ layoutMode }: { layoutMode: LAYOUT }) => {
  const sidebar = useUIStore((state) => state.sidebar)
  const isMobile = useIsMobile()
  // 有 h5 側邊欄
  const hasMobileSidebar = Boolean(layoutMode & LAYOUT.H5_SIDEBAR)
  // 有 pc 側邊欄
  const hasPcSidebar = Boolean(layoutMode & LAYOUT.PC_SIDEBAR)

  // 關閉
  const onClose = () => {
    setUIState({ sidebar: false })
  }

  useEffect(() => {
    // 寬度不是手機模式時，關閉側邊欄
    if (!isMobile) {
      setUIState({ sidebar: false })
    }
  }, [isMobile])

  useEffect(() => {
    const body = document.body
    if (sidebar && isMobile) {
      // 開啟側邊欄時，禁止 body 捲動
      body.classList.add('overflow-hidden')
    } else {
      // 關閉側邊欄時，恢復 body 捲動
      body.classList.remove('overflow-hidden')
    }
  }, [sidebar, isMobile])

  return (
    <AnimatePresence>
      {/* h5 側邊欄展開時的遮罩 */}
      {sidebar && isMobile && hasMobileSidebar && (
        <motion.div
          key="bg"
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
          exit={{ opacity: 0, transition: { duration: 0.3 } }}
          className="fixed top-0 right-0 bottom-0 left-0 z-160 h-full w-full bg-[#00000099]"
          onClick={onClose}
        />
      )}
      {/* pc 要有側邊 layout 或 h5 要有側邊欄 layout 才會顯示側邊欄 */}
      {((!isMobile && hasPcSidebar) || (sidebar && isMobile && hasMobileSidebar)) && <SidebarContent />}
    </AnimatePresence>
  )
})

export default Sidebar
